import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { Account } from "model";

/**
* @class OneTimeWireTransfer
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.oneTimeWireTransfers[0];
* const contactInfo = new OneTimeWireTransfer(record);
* @property {NUMBER} _id - The id of the OneTimeWireTransfer.
*/
export default class OneTimeWireTransfer extends BCMSRecord {
    _id
    _status
    _numberOfPendingApprovals
    _fundingAccount
    _amount
    _currency
    _date
    _reasonForPayment
    _memo

    _name
    _initiatedBy
    _nickname
    _payeeType
    _sendEmailWhenWireStatusDownloaded
    _payeeEmailAddress
    _payeeAccountNumber
    _payeeConfirmAccountNumber
    
    _addressType
    _addressLine1
    _addressLine2
    _city
    _state
    _zip
    _stateOrProvidence
    _postalCode
    _country

    _financialInstitutionNumberType
    _financialInstitutionName
    _financialInstitutionNumber
    _financialInstitutionAddressLine1
    _financialInstitutionAddressLine2
    _financialInstitutionCity
    _financialInstitutionState
    _financialInstitutionZip
    _financialInstitutionStateOrProvidence
    _financialInstitutionPostalCode
    _financialInstitutionCountry
    _financialInstitutionBeneficiaryReference
    _financialInstitutionMessageToBeneficiary1
    _financialInstitutionMessageToBeneficiary2
    _financialInstitutionMessageToBeneficiary3
    _financialInstitutionMessageToBeneficiary4

    _receivingInstitutionNumberType
    _receivingInstitutionName
    _receivingInstitutionNumber
    _receivingInstitutionAddressLine1
    _receivingInstitutionAddressLine2
    _receivingInstitutionCity
    _receivingInstitutionState
    _receivingInstitutionZip
    _receivingInstitutionStateOrProvidence
    _receivingInstitutionPostalCode
    _receivingInstitutionCountry

    _intermediaryInstitutionNumberType
    _intermediaryInstitutionName
    _intermediaryInstitutionNumber
    _intermediaryInstitutionAddressLine1
    _intermediaryInstitutionAddressLine2
    _intermediaryInstitutionCity
    _intermediaryInstitutionState
    _intermediaryInstitutionZip
    _intermediaryInstitutionStateOrProvidence
    _intermediaryInstitutionPostalCode
    _intermediaryInstitutionCountry

    _requiresReceivingFinancialInstitutionDetails
    _requiresIntermediaryFinancialInstitutionDetails
    _referenceNumber;

    constructor(record) {
      super(record) 
    }
  }

  OneTimeWireTransfer.define({
    "recordType": "oneTimeWireTransfers",
    "recordIDProp": "id",
    "className" : "OneTimeWireTransfer",
    "recordProperties":[
      /** 
       * @name id
       * @instance
       * @property get {NUMBER} - gets the id value
       * @property set {NUMBER} - sets the id value
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToNumber("id","_id"),

      /** 
       * @name status
       * @instance
       * @property get {NUMBER} - gets the status value
       * @property set {NUMBER} - sets the status value
       * @memberof OneTimeWireTransfer
       */
        BCMSRecordProperty.mapToString("status","_status"),

      /** 
       * @name numberOfPendingApprovals
       * @instance
       * @property get {NUMBER} - gets the numberOfPendingApprovals value
       * @property set {NUMBER} - sets the numberOfPendingApprovals value
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("numberOfPendingApprovals","_numberOfPendingApprovals"),

        /** 
       * @name companyAccount
       * @instance
       * @property get {STRING} - gets the offsetAccount value
       * @property set {STRING} - sets the offsetAccount value
       * @memberof OneTimeACHPayment
       */
        BCMSRecordProperty.mapToClass("fundingAccount", "_fundingAccount", () => Account),

      /** 
       * @name amount
       * @instance
       * @property get {STRING} - gets the offsetAccount value
       * @property set {STRING} - sets the offsetAccount value
       * @memberof OneTimeACHPayment
       */
      BCMSRecordProperty.mapToCurrency("amount", "_amount"),

      /** 
       * @name currency
       * @instance
       * @property get {STRING} - gets the currency value
       * @property set {STRING} - sets the currency value
       * @memberof OneTimeACHPayment
       */
      BCMSRecordProperty.mapToString("currency", "_currency"),

      /** 
       * @name date
       * @instance
       * @property get {STRING} - gets the offsetAccount value
       * @property set {STRING} - sets the offsetAccount value
       * @memberof OneTimeACHPayment
       */
      BCMSRecordProperty.mapToDate("date", "_date"),

      /** 
       * @name reasonForPayment
       * @instance
       * @property get {STRING} - gets the name
       * @property set {STRING} - sets the name
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("reasonForPayment","_reasonForPayment"),

      /** 
       * @name memo
       * @instance
       * @property get {STRING} - gets the name
       * @property set {STRING} - sets the name
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("memo","_memo"),

      /** 
       * @name name
       * @instance
       * @property get {STRING} - gets the name
       * @property set {STRING} - sets the name
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("name","_name"),

      /** 
       * @name initiatedBy
       * @instance
       * @property get {STRING} - gets the name
       * @property set {STRING} - sets the name
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("initiatedBy","_initiatedBy"),

      /** 
       * @name payeeType
       * @instance
       * @property get {STRING} - gets the payeeType
       * @property set {STRING} - sets the payeeType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("payeeType","_payeeType"),

      /** 
       * @name sendEmailWhenWireStatusDownloaded
       * @instance
       * @property get {STRING} - gets the payeeType
       * @property set {STRING} - sets the payeeType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToBoolean("sendEmailWhenWireStatusDownloaded", "_sendEmailWhenWireStatusDownloaded"),

      /** 
       * @name payeeEmailAddress
       * @instance
       * @property get {STRING} - gets the addressType
       * @property set {STRING} - sets the addressType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("payeeEmailAddress","_payeeEmailAddress"),

      /** 
       * @name payeeAccountNumber
       * @instance
       * @property get {STRING} - gets the addressType
       * @property set {STRING} - sets the addressType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("payeeAccountNumber","_payeeAccountNumber"),

      /** 
       * @name payeeConfirmAccountNumber
       * @instance
       * @property get {STRING} - gets the addressType
       * @property set {STRING} - sets the addressType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("payeeConfirmAccountNumber","_payeeConfirmAccountNumber"),

      /** 
       * @name addressType
       * @instance
       * @property get {STRING} - gets the addressType
       * @property set {STRING} - sets the addressType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("addressType","_addressType"),
      /** 
       * @name addressLine1
       * @instance
       * @property get {STRING} - gets the addressLine1
       * @property set {STRING} - sets the addressLine1
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("addressLine1","_addressLine1"),
      /** 
       * @name addressLine2
       * @instance
       * @property get {STRING} - gets the addressLine2
       * @property set {STRING} - sets the addressLine2
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("addressLine2","_addressLine2"),
      /** 
       * @name city
       * @instance
       * @property get {STRING} - gets the city
       * @property set {STRING} - sets the city
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("city","_city"),
      /** 
       * @name state
       * @instance
       * @property get {STRING} - gets the state
       * @property set {STRING} - sets the state
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("state","_state"),
      /** 
       * @name zip
       * @instance
       * @property get {STRING} - gets the zip
       * @property set {STRING} - sets the zip
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("zip","_zip"),

      /** 
       * @name stateOrProvidence
       * @instance
       * @property get {STRING} - gets the stateOrProvidence
       * @property set {STRING} - sets the stateOrProvidence
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("stateOrProvidence","_stateOrProvidence"),

      /** 
       * @name postalCode
       * @instance
       * @property get {STRING} - gets the postalCode
       * @property set {STRING} - sets the postalCode
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("postalCode","_postalCode"),

      /** 
       * @name country
       * @instance
       * @property get {STRING} - gets the country
       * @property set {STRING} - sets the country
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("country","_country"),

      /** 
       * @name financialInstitutionNumberType
       * @instance
       * @property get {STRING} - gets the financialInstitutionNumberType
       * @property set {STRING} - sets the financialInstitutionNumberType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionNumberType","_financialInstitutionNumberType"),
      /** 
       * @name financialInstitutionName
       * @instance
       * @property get {STRING} - gets the financialInstitutionName
       * @property set {STRING} - sets the financialInstitutionName
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionName","_financialInstitutionName"),
      /** 
       * @name financialInstitutionNumber
       * @instance
       * @property get {STRING} - gets the financialInstitutionNumber
       * @property set {STRING} - sets the financialInstitutionNumber
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionNumber","_financialInstitutionNumber"),
      /** 
       * @name financialInstitutionAddressLine1
       * @instance
       * @property get {STRING} - gets the financialInstitutionAddressLine1
       * @property set {STRING} - sets the financialInstitutionAddressLine1
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionAddressLine1","_financialInstitutionAddressLine1"),
      /** 
       * @name financialInstitutionAddressLine2
       * @instance
       * @property get {STRING} - gets the financialInstitutionAddressLine2
       * @property set {STRING} - sets the financialInstitutionAddressLine2
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionAddressLine2","_financialInstitutionAddressLine2"),
      /** 
       * @name financialInstitutionCity
       * @instance
       * @property get {STRING} - gets the financialInstitutionCity
       * @property set {STRING} - sets the financialInstitutionCity
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionCity","_financialInstitutionCity"),
      /** 
       * @name financialInstitutionState
       * @instance
       * @property get {STRING} - gets the financialInstitutionState
       * @property set {STRING} - sets the financialInstitutionState
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionState","_financialInstitutionState"),
      /** 
       * @name financialInstitutionZip
       * @instance
       * @property get {STRING} - gets the financialInstitutionZip
       * @property set {STRING} - sets the financialInstitutionZip
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionZip","_financialInstitutionZip"),

      /** 
       * @name financialInstitutionStateOrProvidence
       * @instance
       * @property get {STRING} - gets the financialInstitutionStateOrProvidence
       * @property set {STRING} - sets the financialInstitutionStateOrProvidence
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionStateOrProvidence","_financialInstitutionStateOrProvidence"),

      /** 
       * @name financialInstitutionPostalCode
       * @instance
       * @property get {STRING} - gets the financialInstitutionPostalCode
       * @property set {STRING} - sets the financialInstitutionPostalCode
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionPostalCode","_financialInstitutionPostalCode"),

      /** 
       * @name financialInstitutionCountry
       * @instance
       * @property get {STRING} - gets the financialInstitutionCountry
       * @property set {STRING} - sets the financialInstitutionCountry
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionCountry","_financialInstitutionCountry"),

      /** 
       * @name financialInstitutionBeneficiaryReference
       * @instance
       * @property get {STRING} - gets the financialInstitutionBeneficiaryReference
       * @property set {STRING} - sets the financialInstitutionBeneficiaryReference
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionBeneficiaryReference","_financialInstitutionBeneficiaryReference"),

      /** 
       * @name financialInstitutionMessageToBeneficiary1
       * @instance
       * @property get {STRING} - gets the financialInstitutionMessageToBeneficiary1
       * @property set {STRING} - sets the financialInstitutionMessageToBeneficiary1
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionMessageToBeneficiary1","_financialInstitutionMessageToBeneficiary1"),

      /** 
       * @name financialInstitutionMessageToBeneficiary2
       * @instance
       * @property get {STRING} - gets the financialInstitutionMessageToBeneficiary2
       * @property set {STRING} - sets the financialInstitutionMessageToBeneficiary2
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionMessageToBeneficiary2","_financialInstitutionMessageToBeneficiary2"),

      /** 
       * @name financialInstitutionMessageToBeneficiary3
       * @instance
       * @property get {STRING} - gets the financialInstitutionMessageToBeneficiary3
       * @property set {STRING} - sets the financialInstitutionMessageToBeneficiary3
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionMessageToBeneficiary3","_financialInstitutionMessageToBeneficiary3"),

      /** 
       * @name financialInstitutionMessageToBeneficiary4
       * @instance
       * @property get {STRING} - gets the financialInstitutionMessageToBeneficiary4
       * @property set {STRING} - sets the financialInstitutionMessageToBeneficiary4
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("financialInstitutionMessageToBeneficiary4","_financialInstitutionMessageToBeneficiary4"),

      /** 
       * @name receivingInstitutionNumberType
       * @instance
       * @property get {STRING} - gets the receivingInstitutionNumberType
       * @property set {STRING} - sets the receivingInstitutionNumberType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionNumberType","_receivingInstitutionNumberType"),
      /** 
       * @name receivingInstitutionName
       * @instance
       * @property get {STRING} - gets the receivingInstitutionName
       * @property set {STRING} - sets the receivingInstitutionName
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionName","_receivingInstitutionName"),
      /** 
       * @name receivingInstitutionNumber
       * @instance
       * @property get {STRING} - gets the receivingInstitutionNumber
       * @property set {STRING} - sets the receivingInstitutionNumber
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionNumber","_receivingInstitutionNumber"),
      /** 
       * @name receivingInstitutionAddressLine1
       * @instance
       * @property get {STRING} - gets the receivingInstitutionAddressLine1
       * @property set {STRING} - sets the receivingInstitutionAddressLine1
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionAddressLine1","_receivingInstitutionAddressLine1"),
      /** 
       * @name receivingInstitutionAddressLine2
       * @instance
       * @property get {STRING} - gets the receivingInstitutionAddressLine2
       * @property set {STRING} - sets the receivingInstitutionAddressLine2
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionAddressLine2","_receivingInstitutionAddressLine2"),
      /** 
       * @name receivingInstitutionCity
       * @instance
       * @property get {STRING} - gets the receivingInstitutionCity
       * @property set {STRING} - sets the receivingInstitutionCity
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionCity","_receivingInstitutionCity"),
      /** 
       * @name receivingInstitutionState
       * @instance
       * @property get {STRING} - gets the receivingInstitutionState
       * @property set {STRING} - sets the receivingInstitutionState
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionState","_receivingInstitutionState"),
      /** 
       * @name receivingInstitutionZip
       * @instance
       * @property get {STRING} - gets the receivingInstitutionZip
       * @property set {STRING} - sets the receivingInstitutionZip
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionZip","_receivingInstitutionZip"),

      /** 
       * @name receivingInstitutionStateOrProvidence
       * @instance
       * @property get {STRING} - gets the receivingInstitutionStateOrProvidence
       * @property set {STRING} - sets the receivingInstitutionStateOrProvidence
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionStateOrProvidence","_receivingInstitutionStateOrProvidence"),

      /** 
       * @name receivingInstitutionPostalCode
       * @instance
       * @property get {STRING} - gets the receivingInstitutionPostalCode
       * @property set {STRING} - sets the receivingInstitutionPostalCode
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionPostalCode","_receivingInstitutionPostalCode"),

      /** 
       * @name receivingInstitutionCountry
       * @instance
       * @property get {STRING} - gets the receivingInstitutionCountry
       * @property set {STRING} - sets the receivingInstitutionCountry
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("receivingInstitutionCountry","_receivingInstitutionCountry"),

      /** 
       * @name intermediaryInstitutionNumberType
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionNumberType
       * @property set {STRING} - sets the intermediaryInstitutionNumberType
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionNumberType","_intermediaryInstitutionNumberType"),
      /** 
       * @name intermediaryInstitutionName
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionName
       * @property set {STRING} - sets the intermediaryInstitutionName
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionName","_intermediaryInstitutionName"),
      /** 
       * @name intermediaryInstitutionNumber
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionNumber
       * @property set {STRING} - sets the intermediaryInstitutionNumber
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionNumber","_intermediaryInstitutionNumber"),
      /** 
       * @name intermediaryInstitutionAddressLine1
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionAddressLine1
       * @property set {STRING} - sets the intermediaryInstitutionAddressLine1
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionAddressLine1","_intermediaryInstitutionAddressLine1"),
      /** 
       * @name intermediaryInstitutionAddressLine2
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionAddressLine2
       * @property set {STRING} - sets the intermediaryInstitutionAddressLine2
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionAddressLine2","_intermediaryInstitutionAddressLine2"),
      /** 
       * @name intermediaryInstitutionCity
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionCity
       * @property set {STRING} - sets the intermediaryInstitutionCity
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionCity","_intermediaryInstitutionCity"),
      /** 
       * @name intermediaryInstitutionState
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionState
       * @property set {STRING} - sets the intermediaryInstitutionState
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionState","_intermediaryInstitutionState"),
      /** 
       * @name intermediaryInstitutionZip
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionZip
       * @property set {STRING} - sets the intermediaryInstitutionZip
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionZip","_intermediaryInstitutionZip"),

      /** 
       * @name intermediaryInstitutionStateOrProvidence
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionStateOrProvidence
       * @property set {STRING} - sets the intermediaryInstitutionStateOrProvidence
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionStateOrProvidence","_intermediaryInstitutionStateOrProvidence"),

      /** 
       * @name intermediaryInstitutionPostalCode
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionPostalCode
       * @property set {STRING} - sets the intermediaryInstitutionPostalCode
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionPostalCode","_intermediaryInstitutionPostalCode"),

      /** 
       * @name intermediaryInstitutionCountry
       * @instance
       * @property get {STRING} - gets the intermediaryInstitutionCountry
       * @property set {STRING} - sets the intermediaryInstitutionCountry
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("intermediaryInstitutionCountry","_intermediaryInstitutionCountry"),

      /** 
       * @name requiresReceivingFinancialInstitutionDetails
       * @instance
       * @property get {STRING} - gets the receivingInstitutionZip
       * @property set {STRING} - sets the receivingInstitutionZip
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToBoolean("requiresReceivingFinancialInstitutionDetails","_requiresReceivingFinancialInstitutionDetails"),
      /** 
       * @name requiresIntermediaryFinancialInstitutionDetails
       * @instance
       * @property get {STRING} - gets the requiresIntermediaryFinancialInstitutionDetails
       * @property set {STRING} - sets the requiresIntermediaryFinancialInstitutionDetails
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToBoolean("requiresIntermediaryFinancialInstitutionDetails","_requiresIntermediaryFinancialInstitutionDetails"),

      /** 
       * @name referenceNumber
       * @instance
       * @property get {STRING} - gets the referenceNumber
       * @property set {STRING} - sets the referenceNumber
       * @memberof OneTimeWireTransfer
       */
      BCMSRecordProperty.mapToString("referenceNumber","_referenceNumber"),

    ]  
})




